import React from "react";
import styled from "styled-components";
import CaseStudy from '../models/CaseStudy/CaseStudy';
import Quote from "../models/Quote/Quote";
import { AnimateIn, Container, TitleSet, fontSizes, media } from '../ui-components';
import renderRichText from '../utils/render-rich-text';
import CaseStudyCard from './case-study-card';
import ClientQuote from './client-quote';
import ImageWithCaptionTemplate from './image-with-caption';
import Video from './video';
import Icon from './icon';

/**
 * Renders the How We Can Help Section
 * 
 * @component HowWeCanHelpSection 
 * 
 * @param props
 * 
 * @prop {object} howWeCanHelp
 * @prop {string} geoSpace
 * @prop {string} pageType
 * @prop {boolean} renderIcon
 * 
 * @return {JSX}
 */
const HowWeCanHelpSection = props => {
  const { howWeCanHelp, geoSpace, pageType, renderIcon = false } = props;
  const { howWeCanHelpLabel, howWeCanHelpTitle, howWeCanHelpBody, howWeCanHelpContent } = howWeCanHelp;
  const contentType = howWeCanHelp?.howWeCanHelpContent?.internal?.type;

  const HowWeCanHelpContent = props => {
    const { type, content } = props;

    switch (type) {
      case 'ContentfulQuoteBlock':
        return <ClientQuote quote={new Quote(content)}></ClientQuote>;
      case 'ContentfulVideo':
        return <Video title={content.title} embedUrl={content.embedUrl} />;
      case 'ContentfulImageWithCaption':
        return <ImageWithCaptionTemplate image={content.image.url} alt={content.caption} />;
      case 'ContentfulCaseStudy':
        return <CaseStudyCard caseStudy={new CaseStudy(content)} />;
      default:
        return null;
    }
  };

  return (
    <HowWeCanHelpSectionWrapper>
      <AnimateIn onScroll>
        <HowWeCanHelpContainer
          id="how-we-can-help"
          quoteBlock={contentType === 'ContentfulQuoteBlock'}
          imageBlock={contentType === 'ContentfulImageWithCaption'}
          caseStudyBlock={contentType === 'ContentfulCaseStudy'}
          sectionContainer
        >
          {renderIcon && contentType === 'ContentfulQuoteBlock' && <Icon horizontal={'right'} vertical={'top'} pageType={pageType} />}
          <TextContent>
            <TitleSet
              id="help"
              label={howWeCanHelpLabel ? howWeCanHelpLabel : 'How We Can Help'}
              title={howWeCanHelpTitle && howWeCanHelpTitle}
              bodyText={howWeCanHelpBody && renderRichText(howWeCanHelpBody, geoSpace)}
              alignLeft
            />
          </TextContent>
          <RightContent
            quoteBlock={contentType === 'ContentfulQuoteBlock'}
            imageBlock={contentType === 'ContentfulImageWithCaption'}
          >
            {howWeCanHelpContent && <HowWeCanHelpContent type={contentType} content={howWeCanHelpContent} />}
          </RightContent>
        </HowWeCanHelpContainer>
      </AnimateIn>
    </HowWeCanHelpSectionWrapper>
  );
};

const HowWeCanHelpSectionWrapper = styled.div`
  /* This div is needed to clip the overflow of the icons if rendered */
  overflow: clip visible;
`;

const HowWeCanHelpContainer = styled(Container)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: ${({ caseStudyBlock }) => (caseStudyBlock ? 'flex-start' : 'center')};
  gap: ${({ imageBlock }) => (imageBlock ? '0' : '80px')};

  @media screen and (max-width: ${media.largeTop}) {
    flex-direction: ${({ quoteBlock }) => (quoteBlock ? 'column' : 'row')};
  }

  @media screen and (max-width: ${media.largeBottom}) {
    flex-direction: column;
    align-items: center;
    gap: ${({ imageBlock }) => (imageBlock ? '20px' : '')};
  }

  @media screen and (max-width: ${media.mediumBottom}) {
    gap: ${({ imageBlock }) => (imageBlock ? '20px' : '40px')};
    padding-bottom: ${({ imageBlock }) => (imageBlock ? '20px' : '')};
  }
`;

const TextContent = styled.div`
  max-width: 640px;
  z-index: 2;

  // Override styles being applied in render rich text
  p {
    font-size: ${fontSizes.paragraph.large.fontSize};
    line-height: ${fontSizes.paragraph.large.lineHeight};

    @media screen and (max-width: ${media.mediumBottom}) {
      font-size: ${fontSizes.paragraph.large.mobile.fontSize};
      line-height: ${fontSizes.paragraph.large.mobile.lineHeight};
    }
  }

  @media screen and (max-width: ${media.largeTop}) {
    max-width: 100%;
  }
`;

const RightContent = styled.div`
  flex: ${({ quoteBlock, imageBlock }) => (quoteBlock || imageBlock ? '' : '1')};
  min-width: 400px;

  @media screen and (max-width: ${media.largeBottom}) {
    min-width: auto;
  }

  .client-quote {
    /* Quotes need a top margin to prevent overlap with decorative icons */
    margin-top: 40px;

    @media screen and (max-width: ${media.largeTop}) {
      margin-top: 0px;
    }
  }

  .image-with-caption {
    max-height: 600px;
    max-width: 600px;
    width: auto;
    height: auto;

    img {
      margin: 0;
      object-fit: cover;
    }

    @media screen and (max-width: ${media.largeBottom}) {
      /* Keep bottom spacing when switching to column layout */
      /* margin-bottom: 40px; */
    }

    @media screen and (max-width: ${media.mediumBottom}) {
      min-height: 300px;
      /* Remove margin for tab;et/mobile view */
      margin: 0;
    }
  }
`;

export default HowWeCanHelpSection;
